import { FIRST_RESULT_PENDING_LABEL } from './startMeasuring';

/**
 * Case-detail HOME status: current Scan, latest signed, Control, new activity.
 * Presentation only — uses persisted Case/Scan facts already on the page.
 */

export const HOME_CURRENT_SCAN_EMPTY = 'No Safety Scan awaiting review.';
export const HOME_LATEST_SIGNED_EMPTY = 'No signed Safety Scan yet.';
export const HOME_CONTROL_EMPTY = 'No Control has been recorded for this Safety Case.';
export const HOME_CONTROL_NONE_IN_FORCE = 'No control currently in force';

export type SafetyCaseHomeStatusFacts = {
  scanWindowLabel?: string | null;
  hasActiveDraft?: boolean;
  currentScanEstablished?: boolean;
  currentScanHref?: string | null;
  latestRunLabel?: string | null;
  latestRunStatus?: string | null;
  scanOutcomeLabel?: string | null;
  scanOutcomeReason?: string | null;
  scanCreatedHref?: string | null;
  latestSignedWindow?: string | null;
  hasSignedScan?: boolean;
  latestSignedHref?: string | null;
  controlTitle?: string | null;
  controlMissing?: boolean;
  controlNoneInForce?: boolean;
  hasNewActivity?: boolean;
  primaryCtaLabel?: string | null;
  primaryCtaIsAction?: boolean;
  primaryCtaKind?: string | null;
  nextScheduledLabel?: string | null;
  scanCount?: number | null;
  controlOwner?: string | null;
};

export type SafetyCaseHomeStatusView = {
  currentScanLabel: string;
  currentScanEmpty: boolean;
  currentScanHref: string | null;
  latestRunLabel: string | null;
  latestRunStatus: string | null;
  scanOutcomeLabel: string | null;
  scanOutcomeReason: string | null;
  scanCreatedHref: string | null;
  latestSignedLabel: string;
  latestSignedEmpty: boolean;
  latestSignedHref: string | null;
  controlLabel: string;
  controlEmpty: boolean;
  newActivityLabel: string;
  primaryCtaLabel: string;
  primaryCtaIsAction: boolean;
  nextScheduledLabel: string | null;
  scanCountLabel: string | null;
  controlOwnerLabel: string | null;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function readStoredScanWindow(row: Record<string, unknown> | null): { start: string | null; end: string | null } {
  if (!row) return { start: null, end: null };
  const start = text(
    row.window_start_utc || row.scope_start || row.scopeStart || row.windowStartUtc,
  ) || null;
  const end = text(
    row.window_end_utc || row.scope_end || row.scopeEnd || row.windowEndUtc,
  ) || null;
  return { start, end };
}

/** Current Scan identity window: active draft Scan, not queue or Case time_window. */
export function resolveActiveDraftScanWindow(args: {
  activeDraftReportId?: string | null;
  scanHistory?: unknown;
  firstEligibleScan?: unknown;
  reportOpenWindow?: { start?: string | null; end?: string | null } | null;
}): { start: string | null; end: string | null } {
  const draftId = text(args.activeDraftReportId);
  if (!draftId) return { start: null, end: null };

  const history = Array.isArray(args.scanHistory) ? args.scanHistory : [];
  const historyRow = history
    .map((row) => asRecord(row))
    .find((row) => row && text(row.report_id || row.scan_id || row.reportId) === draftId) || null;
  const fromHistory = readStoredScanWindow(historyRow);
  if (fromHistory.start && fromHistory.end) return fromHistory;

  const first = asRecord(args.firstEligibleScan);
  if (first && text(first.report_id || first.scan_id || first.reportId) === draftId) {
    const fromFirst = readStoredScanWindow(first);
    if (fromFirst.start && fromFirst.end) return fromFirst;
  }

  const openStart = text(args.reportOpenWindow?.start) || null;
  const openEnd = text(args.reportOpenWindow?.end) || null;
  if (openStart && openEnd) return { start: openStart, end: openEnd };

  if (fromHistory.start || fromHistory.end) return fromHistory;
  return { start: openStart, end: openEnd };
}

export function buildSafetyCaseHomeStatus(
  facts: SafetyCaseHomeStatusFacts,
): SafetyCaseHomeStatusView {
  const hasCurrent = Boolean(facts.hasActiveDraft);
  const windowLabel = text(facts.scanWindowLabel);
  const currentScanLabel = hasCurrent
    ? (windowLabel || 'Current Safety Scan')
    : (facts.scanCount === 0 ? FIRST_RESULT_PENDING_LABEL : HOME_CURRENT_SCAN_EMPTY);
  const latestWindow = text(facts.latestSignedWindow);
  const latestSignedLabel = facts.hasSignedScan
    ? (latestWindow || 'Signed Safety Scan on file')
    : HOME_LATEST_SIGNED_EMPTY;
  const controlNoneInForce = Boolean(facts.controlNoneInForce);
  const controlEmpty = Boolean(facts.controlMissing) && !controlNoneInForce;
  const controlLabel = controlNoneInForce
    ? (text(facts.controlTitle) || HOME_CONTROL_NONE_IN_FORCE)
    : controlEmpty
      ? HOME_CONTROL_EMPTY
      : (text(facts.controlTitle) || 'Control recorded');

  return {
    currentScanLabel,
    currentScanEmpty: !hasCurrent,
    currentScanHref: hasCurrent && text(facts.currentScanHref) ? text(facts.currentScanHref) : null,
    latestRunLabel: text(facts.latestRunLabel) || null,
    latestRunStatus: text(facts.latestRunStatus) || null,
    scanOutcomeLabel: text(facts.scanOutcomeLabel) || null,
    scanOutcomeReason: text(facts.scanOutcomeReason) || null,
    scanCreatedHref: text(facts.scanCreatedHref) || null,
    latestSignedLabel,
    latestSignedEmpty: !facts.hasSignedScan,
    latestSignedHref: facts.hasSignedScan && text(facts.latestSignedHref)
      ? text(facts.latestSignedHref)
      : null,
    controlLabel,
    controlEmpty,
    newActivityLabel: facts.hasNewActivity ? 'Yes' : 'No',
    primaryCtaLabel: text(facts.primaryCtaLabel) || 'Monitoring active',
    primaryCtaIsAction: Boolean(facts.primaryCtaIsAction),
    nextScheduledLabel: text(facts.nextScheduledLabel) || null,
    scanCountLabel: typeof facts.scanCount === 'number'
      ? `${facts.scanCount} scan${facts.scanCount === 1 ? '' : 's'}`
      : null,
    controlOwnerLabel: text(facts.controlOwner) || null,
  };
}
